import React, { useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { Send, X } from 'lucide-react';

import { aiCoachAPI } from '../../services/api';
import CoachMessageBubble from './CoachMessageBubble';
import ContextBadgePanel from './ContextBadgePanel';
import EmptyCoachState from './EmptyCoachState';
import SuggestedPrompts from './SuggestedPrompts';
import TypingIndicator from './TypingIndicator';

const CoachChatWindow = ({ onClose }) => {
  const [messages, setMessages] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem('aivion_coach_messages') || '[]');
    } catch {
      return [];
    }
  });
  const [input, setInput] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [conversationId, setConversationId] = useState(() => localStorage.getItem('aivion_coach_conversation_id'));
  const inputRef = useRef(null);

  useEffect(() => {
    localStorage.setItem('aivion_coach_messages', JSON.stringify(messages.slice(-24)));
  }, [messages]);

  useEffect(() => {
    if (conversationId) {
      localStorage.setItem('aivion_coach_conversation_id', conversationId);
    }
  }, [conversationId]);

  const sendMessage = async (text) => {
    const content = text.trim();
    if (!content || isTyping) return;

    setMessages((current) => [...current, { role: 'user', content }]);
    setInput('');
    setIsTyping(true);

    try {
      const result = await aiCoachAPI.chat({ message: content, conversationId });
      setConversationId(result.conversation_id);
      setMessages((current) => [
        ...current,
        { role: 'assistant', content: result.response, recommendations: result.recommendations || [] },
      ]);
    } catch (error) {
      setMessages((current) => [
        ...current,
        { role: 'assistant', content: error.message || 'Coach is unavailable right now. Please try again.' },
      ]);
    } finally {
      setIsTyping(false);
      inputRef.current?.focus();
    }
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    sendMessage(input);
  };

  return (
    <motion.section
      initial={{ opacity: 0, y: 24, scale: 0.96 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, y: 24, scale: 0.96 }}
      className="fixed bottom-24 right-4 z-50 flex h-[min(680px,calc(100vh-7rem))] w-[min(420px,calc(100vw-2rem))] flex-col overflow-hidden rounded-lg border border-white/10 bg-gradient-to-br from-[#1e0b3a]/95 via-[#3b136b]/95 to-[#5b1799]/95 shadow-[0_0_44px_rgba(217,70,239,0.34)] backdrop-blur-xl"
      aria-label="AI Career Coach chat"
    >
      <header className="border-b border-white/10 bg-white/5 p-4">
        <div className="flex items-start justify-between gap-3">
          <div>
            <p className="text-sm font-semibold text-white">AI Career Coach</p>
            <p className="text-xs text-purple-100/75">Personalized to your AIVIONLearn journey</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md border border-white/10 bg-white/5 p-2 text-purple-100 transition hover:bg-white/10"
            aria-label="Close AI coach"
          >
            <X size={16} aria-hidden="true" />
          </button>
        </div>
        <div className="mt-3">
          <ContextBadgePanel />
        </div>
      </header>

      <div className="flex-1 space-y-4 overflow-y-auto p-4">
        {messages.length === 0 && (
          <>
            <EmptyCoachState />
            <SuggestedPrompts onSelect={sendMessage} />
          </>
        )}
        {messages.map((message, index) => (
          <div key={`${message.role}-${index}`} className="space-y-2">
            <CoachMessageBubble message={message} />
            {message.role === 'assistant' && Array.isArray(message.recommendations) && message.recommendations.length > 0 && (
              <div className="ml-9 flex flex-wrap gap-2">
                {message.recommendations.map((recommendation) => (
                  <button
                    key={recommendation}
                    type="button"
                    onClick={() => sendMessage(recommendation)}
                    className="rounded-md border border-fuchsia-300/20 bg-fuchsia-400/10 px-2 py-1 text-[11px] text-fuchsia-100 transition hover:bg-fuchsia-400/20"
                  >
                    {recommendation}
                  </button>
                ))}
              </div>
            )}
          </div>
        ))}
        {isTyping && <TypingIndicator />}
      </div>

      <form onSubmit={handleSubmit} className="border-t border-white/10 bg-black/15 p-3">
        <div className="flex gap-2">
          <input
            ref={inputRef}
            value={input}
            onChange={(event) => setInput(event.target.value)}
            placeholder="Ask your coach..."
            className="min-w-0 flex-1 rounded-md border border-white/10 bg-black/25 px-3 py-2 text-sm text-white outline-none placeholder:text-purple-100/50 focus:border-fuchsia-300/60 focus:ring-2 focus:ring-fuchsia-400/20"
          />
          <button
            type="submit"
            disabled={!input.trim() || isTyping}
            className="inline-flex h-10 w-10 items-center justify-center rounded-md bg-gradient-to-r from-purple-500 to-pink-500 text-white shadow-[0_0_22px_rgba(236,72,153,0.35)] transition hover:-translate-y-0.5 disabled:cursor-not-allowed disabled:opacity-50"
            aria-label="Send message"
          >
            <Send size={16} aria-hidden="true" />
          </button>
        </div>
      </form>
    </motion.section>
  );
};

export default CoachChatWindow;
